import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { SettingsContext } from "../contexts/index.jsx";
import { RCListingForm } from "../forms";
import { Loading } from "../components";
import StatePanel from "../components/ui/StatePanel";
import { requireResponse } from "../core/errors/requireResponse";

const RCListingCreate = () => {
  const history = useHistory();
  const location = useLocation();
  const settings = useContext(SettingsContext);

  const { baseProductId } = useParams();
  const [product, setProduct] = useState();
  // The product failed to load: show a retry, not the spinner forever.
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // A seed listing handed over by a flow that collected values OUTSIDE the form
  // — today, voice dictation. It has to reach RCListingForm as the `listing`
  // prop: the form memoises initialValues and runs with enableReinitialize, so
  // values written into Formik after mount are wiped on the first re-render.
  const seedListing = location.data?.listing ?? null;

  useEffect(() => {
    setProduct(null);
    setLoadFailed(false);

    if (location.data?.product) {
      setProduct(location.data.product);
      return;
    }

    if (baseProductId === "new") {
      setProduct({});
      return;
    }

    if (!baseProductId) {
      return;
    }

    let alive = true;
    const loadProduct = async () => {
      try {
        const { data } = requireResponse(await axios.get(`/api/products/${baseProductId}`));
        if (alive) setProduct(data);
      } catch {
        if (alive) setLoadFailed(true);
      }
    };

    loadProduct();
    return () => {
      alive = false;
    };
  }, [baseProductId, reloadKey]);

  const afterSubmit = (listing) =>
    history.push("/product/" + listing.id + `?${settings.iframeQueryParams}`);

  const formProps = {
    listing: { ...seedListing, product },
    editableProduct: true,
    afterSubmit,
  };

  return (
    <>
      <Helmet>
        <title>Create Listing — Retail Circle</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>

      {baseProductId && !product && loadFailed ? (
        <StatePanel
          state="error"
          title="Couldn't load this product"
          description="Something went wrong on our side — try again."
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : baseProductId && !product ? (
        <div style={{ padding: "60px 0", display: "flex", justifyContent: "center" }}>
          <Loading type="loading" showText={false} />
        </div>
      ) : (
        <RCListingForm {...formProps} />
      )}
    </>
  );
};

export default RCListingCreate;
